import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Textarea } from '@/components/Controls/Textarea';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { ChangeEvent, useEffect } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { useGetGroupsSelectOptions } from '@/hooks/group.hooks';
import { Select } from '@/components/Controls/Select';
import { Checkbox } from '@/components/Controls/Checkbox';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { getTranslatedItem } from '@/utils/language.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useGetMatchingVehiculeTypeError } from '@/hooks/vehicule-check-type.hooks';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';

export const VehiculeCheckTypeInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const translatedVehiculeCheckType = getTranslatedItem(
    data?.translatedVehiculeCheckTypes,
  );

  const { getGroupsSelectOptions } = useGetGroupsSelectOptions();

  /**
   * === Set Value Watchers section ===
   */
  const nameValue = watch && watch('name' as Path<TSchema>);

  const activeValue = watch && watch('active' as Path<TSchema>);

  const personTypesValue =
    watch && watch('personTypes' as Path<TSchema>, data?.personTypes ?? []);

  const detectLicensePlateInVideoValue =
    watch &&
    watch(
      'detectLicensePlateInVideo' as Path<TSchema>,
      data?.detectLicensePlateInVideo ?? false,
    );

  const displayCheckpointHelpVideosValue =
    watch &&
    watch(
      'displayCheckpointHelpVideos' as Path<TSchema>,
      data?.displayCheckpointHelpVideos ?? true,
    );

  /**
   * === Set Value Watchers section ===
   */

  const { data: matchingVehiculeCheckTypeError } =
    useGetMatchingVehiculeTypeError({
      id: data?.id,
      name: nameValue,
    });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('groupId' as Path<TSchema>, data.groupId ?? '');
      setValue('icon' as Path<TSchema>, data.icon ?? '');
      setValue(
        'name' as Path<TSchema>,
        (translatedVehiculeCheckType?.name || data.name) ?? '',
      );
      setValue(
        'shortDescription' as Path<TSchema>,
        (translatedVehiculeCheckType?.shortDescription ||
          data.shortDescription) ??
          '',
      );
      setValue(
        'longDescription' as Path<TSchema>,
        (translatedVehiculeCheckType?.longDescription ||
          data.longDescription) ??
          '',
      );
      setValue('personTypes' as Path<TSchema>, data.personTypes ?? []);
      setValue(
        'detectLicensePlateInVideo' as Path<TSchema>,
        data.detectLicensePlateInVideo ?? false,
      );
      setValue(
        'displayCheckpointHelpVideos' as Path<TSchema>,
        data.displayCheckpointHelpVideos ?? true,
      );
      setValue('active' as Path<TSchema>, data.active);
    }
  }, [setValue, data, translatedVehiculeCheckType]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  const onCheckPersonType = (e: ChangeEvent<HTMLInputElement>) => {
    const type = e.currentTarget.value as ActionUserType;
    let selectedTypes = personTypesValue || [];

    if (personTypesValue?.includes(type)) {
      selectedTypes = personTypesValue?.filter(
        (selectedType) => selectedType !== type,
      );
    } else {
      selectedTypes = [...selectedTypes, type];
    }

    setValue(
      'personTypes' as Path<TSchema>,
      selectedTypes as PathValue<TSchema, Path<TSchema>>,
    );
  };

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const vehiculeCheckTypeErrorMessages =
      matchingVehiculeCheckTypeError?.data?.errorMessages;

    if (vehiculeCheckTypeErrorMessages?.includes(FieldError.NAME_EXISTS)) {
      setError('name' as Path<TSchema>, {
        message: t('inspectionTypeNameAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection
            text={t('theInspectionType')}
            defaultIconClassName="ki-badge"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <FormField
          text={t('common:group')}
          required
          textWrapperClassName="mt-3 max-md:mt-0"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
            <Controller
              name="groupId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  errorMessage={errors && (errors.groupId?.message as string)}
                  placeholder={t('common:chooseTheGroup')}
                  options={getGroupsSelectOptions()}
                />
              )}
            />
          </div>
        </FormField>
        <FormField
          text={t('inspectionTypeIcon')}
          containerClassName="mb-2 max-md:mb-0"
          textWrapperClassName="mt-3"
        >
          <Avatar
            outerContainerClassName="max-md:w-full"
            containerClassName="max-md:mx-auto"
            imageUrl={data?.icon}
            onChange={(url) =>
              setValue?.(
                'icon' as Path<TSchema>,
                url as PathValue<TSchema, Path<TSchema>>,
              )
            }
          />
        </FormField>
        <FormField text={t('common:name')} required textWrapperClassName="mt-3">
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Input
              {...(register && register('name' as Path<TSchema>))}
              errorMessage={errors && (errors.name?.message as string)}
              placeholder={t('common:name')}
              type="text"
            />
          </div>
        </FormField>
        <FormField
          text={t('common:shortDescription')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Input
              {...(register && register('shortDescription' as Path<TSchema>))}
              errorMessage={
                errors && (errors.shortDescription?.message as string)
              }
              placeholder={t('common:shortDescription')}
              type="text"
            />
          </div>
        </FormField>
        <FormField
          text={t('common:longDescription')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Textarea
              {...(register && register('longDescription' as Path<TSchema>))}
              errorMessage={
                errors && (errors.longDescription?.message as string)
              }
              placeholder={t('common:longDescription')}
            />
          </div>
        </FormField>
        <FormField
          containerClassName="mt-5 mb-0.5"
          text={t('inspectionTypeAvailableFor')}
        >
          <div className="flex flex-1 flex-col items-start gap-3">
            <Checkbox
              value={ActionUserType.PROFESSIONAL}
              text={t('common:professional')}
              checked={personTypesValue?.includes(ActionUserType.PROFESSIONAL)}
              onChange={onCheckPersonType}
            />
            <Checkbox
              value={ActionUserType.INDIVIDUAL}
              text={t('common:individual')}
              checked={personTypesValue?.includes(ActionUserType.INDIVIDUAL)}
              onChange={onCheckPersonType}
            />
          </div>
        </FormField>
        <FormField
          containerClassName="mt-6"
          text={t('detectLicensePlateInVideo')}
        >
          <div className="flex flex-col gap-2.5">
            <Switch
              {...(register &&
                register('detectLicensePlateInVideo' as Path<TSchema>))}
              errorMessage={
                errors && (errors.detectLicensePlateInVideo?.message as string)
              }
              checked={detectLicensePlateInVideoValue}
            />
          </div>
        </FormField>
        <FormField
          containerClassName="mt-6"
          text={t('displayCheckpointHelpVideos')}
        >
          <div className="flex flex-col gap-2.5">
            <Switch
              {...(register &&
                register('displayCheckpointHelpVideos' as Path<TSchema>))}
              errorMessage={
                errors &&
                (errors.displayCheckpointHelpVideos?.message as string)
              }
              checked={displayCheckpointHelpVideosValue}
            />
          </div>
        </FormField>
        <FormField text={t('common:activeStatus')} textWrapperClassName="mt-3">
          <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
            <Switch
              {...(register && register('active' as Path<TSchema>))}
              errorMessage={errors && (errors.active?.message as string)}
              checked={data ? activeValue : true}
            />
          </div>
        </FormField>
      </Card>
    </form>
  );
};
